import { useMemo, useState } from "react";
import { Button, Table } from "antd";
import { Link } from "react-router-dom";
import { env } from "../../../../env/env";
import { DataKitMenuDto, MenuUsageDto } from "../../../../domain/DataKitDto";
import { DataKitMenuDrawer } from "./DataKitMenuDrawer";
import "./DataKitMenusTab.scss";

const MENUS_PATH = () => `${env.dashboardPath}/account/data-kits?tab=menus`;

interface Row {
  menu: DataKitMenuDto;
  kitItems: number;
  kitDesigns: number;
}

interface Props {
  menus: DataKitMenuDto[];
  usage: MenuUsageDto[];
  canWrite: boolean;
  onChanged: () => void;
}

/**
 * The kit's Menus tab: a **usage view** over the account's menu list, not a second list.
 *
 * Menus stay account-level, since one design can show items from more than one kit (decided Oct 6),
 * so a kit lists only the menus whose designs place items from it - listing every account menu made
 * each kit look like it owned menus it has nothing to do with. There is deliberately no creator
 * here; menus are made on the account's own Menus tab. Edit opens the same drawer, so a note or a
 * rename made from a kit still lands on the one shared record.
 */
export function DataKitMenusTab({ menus, usage, canWrite, onChanged }: Props) {
  const [editing, setEditing] = useState<DataKitMenuDto | null>(null);

  const usageByMenuId = useMemo(() => {
    const map = new Map<string, MenuUsageDto>();
    usage.forEach((u) => {
      if (u.menuId) map.set(u.menuId, u);
    });
    return map;
  }, [usage]);

  const rows: Row[] = useMemo(
    () =>
      menus
        .map((menu) => {
          const u = menu.id ? usageByMenuId.get(menu.id) : undefined;
          return {
            menu,
            kitItems: u?.itemCount ?? 0,
            kitDesigns: u?.designCount ?? 0,
          };
        })
        .filter((row) => row.kitDesigns > 0),
    [menus, usageByMenuId]
  );

  /** Designs on a menu that place nothing from this kit — worth saying rather than showing 0. */
  const unlabelled = useMemo(() => usage.find((u) => !u.menuId), [usage]);

  const columns = [
    {
      title: "Menu",
      key: "menu",
      render: (_: unknown, row: Row) => (
        <div className="menus-usage-name-cell">
          <div className="menus-usage-name">{row.menu.name}</div>
          {row.menu.note && <div className="menus-usage-note">{row.menu.note}</div>}
        </div>
      ),
      sorter: (a: Row, b: Row) =>
        a.menu.name.localeCompare(b.menu.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
    },
    {
      title: "Items from this library",
      dataIndex: "kitItems",
      key: "kitItems",
      width: 170,
      align: "center" as const,
      render: (count: number) =>
        count > 0 ? count : <span className="menus-usage-empty">--</span>,
    },
    {
      title: "Designs",
      dataIndex: "kitDesigns",
      key: "kitDesigns",
      width: 120,
      align: "center" as const,
      render: (count: number) =>
        count > 0 ? count : <span className="menus-usage-empty">--</span>,
    },
    {
      title: "",
      key: "action",
      width: 90,
      align: "right" as const,
      render: (_: unknown, row: Row) =>
        canWrite ? (
          <Button className="menus-usage-edit-btn" size="small" onClick={() => setEditing(row.menu)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="data-kit-menus-tab">
      <p className="data-kit-menus-hint">
        Menus whose designs use items from this library. Menus are created and named in{" "}
        <Link to={MENUS_PATH()}>Item Libraries &rsaquo; Menus</Link>.
      </p>

      <Table
        className="data-kit-menus-table"
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row.menu.id || row.menu.name}
        pagination={false}
        locale={{
          emptyText: (
            <div className="menus-usage-empty-state">
              <h3>No menus use this library yet</h3>
              <p>A menu shows here once a design on it uses items from this library.</p>
              <Link to={MENUS_PATH()}>
                <Button>Go to Menus</Button>
              </Link>
            </div>
          ),
        }}
      />

      {unlabelled && unlabelled.designCount > 0 && (
        <p className="menus-usage-unlabelled">
          {unlabelled.designCount} design{unlabelled.designCount === 1 ? "" : "s"} using this library
          {unlabelled.designCount === 1 ? " is" : " are"} not on any menu yet.
        </p>
      )}

      {editing && (
        <DataKitMenuDrawer
          menu={editing}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
